import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { AttachField, AttachViewer } from "../ui/attachments.jsx";
import Agreements from "./Agreements.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { rowToForm, useSaveCustomer, useDeleteCustomer } from "./api.js";

export default function CustomerForm({ cust, onClose }) {
  const isNew = !cust?.id;
  const [f, setF] = useState(() => rowToForm(cust ?? {}));
  const [errs, setErrs] = useState({});
  const [formError, setFormError] = useState("");
  const [viewing, setViewing] = useState(null);
  const { notify } = useToast();

  const save = useSaveCustomer();
  const del = useDeleteCustomer();
  const busy = save.isPending || del.isPending;

  const set = (k, v) => {
    setF((p) => ({ ...p, [k]: v }));
    setErrs((p) => { const n = { ...p }; delete n[k]; return n; });
  };

  async function onSave() {
    setFormError("");
    const e = {};
    if (!f.name.trim()) e.name = "Customer name required";
    // email is NOT NULL in the schema because invoices are emailed there.
    if (!f.email.trim() || !f.email.includes("@")) e.email = "Valid email required — invoices are sent here";
    if (f.cardLast4 && !/^\d{0,4}$/.test(f.cardLast4)) e.card = "Last 4 digits only";
    if (Object.keys(e).length) { setErrs(e); return; }

    try {
      await save.mutateAsync({ id: cust?.id, form: f, previous: cust });
      notify(isNew ? `${f.name.trim()} added.` : "Changes saved.");
      onClose();
    } catch (err) {
      setFormError(err.message);
    }
  }

  async function onDelete() {
    setFormError("");
    try {
      await del.mutateAsync({ id: cust.id, paths: [cust.dl_photo_path, cust.coi_path] });
      notify("Customer deleted.");
      onClose();
    } catch (err) {
      setFormError(err.message);
    }
  }

  return (
    <Modal
      title={isNew ? "Add customer" : f.name || "Edit customer"}
      onClose={onClose}
      wide
      footer={
        <>
          {!isNew && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={onDelete}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Add customer" : "Save changes"}
          </button>
        </>
      }
    >
      {formError && <div className="notice red">{formError}</div>}
      {Object.keys(errs).length > 0 && (
        <div className="notice red">
          <b>Customer information required.</b> Fill in the highlighted fields below.
        </div>
      )}

      <div className="frm">
        <Fld label="Full name" err={errs.name}>
          <input value={f.name} onChange={(e) => set("name", e.target.value)} />
        </Fld>
        <Fld label="Business name">
          <input value={f.business} onChange={(e) => set("business", e.target.value)} />
        </Fld>
        <Fld label="Phone">
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        </Fld>
        <Fld label="Email (required)" err={errs.email} hint="Invoices are emailed here.">
          <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} />
        </Fld>

        <Fld full label="Home / physical address">
          <input
            value={f.homeAddress}
            onChange={(e) => set("homeAddress", e.target.value)}
            placeholder="Street, city, state, ZIP"
          />
        </Fld>
        <Fld full label="Billing address">
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ flex: 1 }}
              value={f.billingAddress}
              onChange={(e) => set("billingAddress", e.target.value)}
              placeholder="Street, city, state, ZIP"
            />
            <button
              type="button"
              className="btn ghost sm"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => set("billingAddress", f.homeAddress)}
            >
              Billing same as home
            </button>
          </div>
        </Fld>

        <Fld label="Driver's license #">
          <input value={f.dlNumber} onChange={(e) => set("dlNumber", e.target.value)} />
        </Fld>
        <Fld label="Card type">
          <select value={f.cardType} onChange={(e) => set("cardType", e.target.value)}>
            <option value="">—</option>
            <option>Visa</option>
            <option>Mastercard</option>
            <option>Amex</option>
            <option>Discover</option>
          </select>
        </Fld>

        <Fld
          full
          label="Card — last 4 digits only"
          err={errs.card}
          hint="Reference only. Full numbers stay in your payment processor — the database rejects anything longer."
        >
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ flex: 1 }}
              maxLength={4}
              inputMode="numeric"
              placeholder="1234"
              value={f.cardLast4}
              onChange={(e) => set("cardLast4", e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            <input
              style={{ flex: 1 }}
              placeholder="Exp MM/YY"
              value={f.cardExp}
              onChange={(e) => set("cardExp", e.target.value)}
            />
          </div>
        </Fld>

        <Fld label="COI expires" hint="Insurance certificate expiry, flagged in the list when past.">
          <input type="date" value={f.coiExpires} onChange={(e) => set("coiExpires", e.target.value)} />
        </Fld>
        <Fld label="Driver's licence photo">
          <AttachField
            path={f.dlPhotoPath}
            folder="customers"
            accept="image/*"
            label="Upload DL"
            camera
            onView={setViewing}
            onChange={({ path }) => set("dlPhotoPath", path)}
          />
        </Fld>

        <Fld full label="Certificate of insurance (COI)">
          <AttachField
            path={f.coiPath}
            name={f.coiName}
            folder="customers"
            accept="image/*,.pdf"
            label="Upload COI"
            onView={setViewing}
            onChange={({ path, name }) => { set("coiPath", path); set("coiName", name); }}
          />
        </Fld>

        <Fld full label="Signed rental agreements">
          <Agreements customerId={cust?.id} onView={setViewing} />
        </Fld>

        <Fld full label="Notes about this customer">
          <textarea
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Preferred contact, jobsite habits, credit terms, past issues…"
          />
        </Fld>

        {viewing && (
          <AttachViewer path={viewing.path} name={viewing.name} onClose={() => setViewing(null)} />
        )}
      </div>
    </Modal>
  );
}
